/**
 * The card a grid row becomes on a phone.
 *
 * One presentation for every list in the app — customers, plants, structures,
 * and the services tier's job-first views — so a row reads the same wherever
 * it appears and nothing it carries can be cut off. Rows size themselves
 * (see resolveRowHeightProps), so the card is free to wrap a long name, carry
 * a second line of detail, list metadata, and hold an action row without any
 * of it clipping or pushing the list sideways.
 *
 * Contains:
 * - GridMobileCard — the card (default export)
 */

import { Box, ListItemButton, Typography } from '@mui/material';
import React from 'react';

import { MOBILE_MIN_ROW_HEIGHT } from './rowHeight';

const cardSx = {
  display: 'flex',
  width: '100%',
  minWidth: 0,
  minHeight: MOBILE_MIN_ROW_HEIGHT,
  // Interactive roots inherit a row from ListItemButton; static roots need the
  // same explicit flex contract. Either way, a card is a stack of bands.
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'center',
  gap: 0.75,
  py: 1.5,
  px: 2,
};

// The header wraps too: when a long status will not fit beside the name, it
// drops to its own line instead of squeezing the name into a column of single
// characters or spilling off the side of the card.
const headerSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  columnGap: 1.5,
  rowGap: 0.5,
  width: '100%',
  minWidth: 0,
};

// A small mark (a status dot) centers against the name's first line rather
// than the top of a wrapped block; a tall mark (a logo) is taller than the
// floor and keeps its own size.
const leadingSx = { flexShrink: 0, display: 'flex', alignItems: 'center', minHeight: '1.5rem' };

// The identity column takes every spare pixel and asks for a readable width
// before the header wraps; minWidth 0 still lets a long unbroken word break
// rather than widen the row.
const IDENTITY_PREFERRED_WIDTH = '9rem';
const identitySx = { flex: `1 1 ${IDENTITY_PREFERRED_WIDTH}`, minWidth: 0 };

const wrappingTextSx = { overflowWrap: 'anywhere' };

const statusSx = { flexShrink: 0, display: 'flex', alignItems: 'center' };

const bandSx = { display: 'flex', flexWrap: 'wrap', minWidth: 0, columnGap: 1.5, rowGap: 0.5 };

/**
 * @param {object} props
 * @param {Function} [props.onClick] - Row activation; the whole card is the target.
 * @param {React.ReactNode} [props.leading] - Status dot, logo, or icon. Sized by
 *   the caller and never given a reserved width, so a card with no image (or a
 *   broken one) closes the gap instead of holding it open.
 * @param {React.ReactNode} props.primary - The row's name. Wraps.
 * @param {React.ReactNode} [props.secondary] - Supporting detail line. Wraps.
 * @param {{key: string, label: React.ReactNode, value: React.ReactNode}[]} [props.meta]
 *   Labelled facts, laid out in a wrapping band. Compose base and extension
 *   entries with composeMobileCardSections before passing them.
 * @param {React.ReactNode[]} [props.chips] - Chips and inline icons; wrapping band.
 * @param {React.ReactNode} [props.status] - Trailing status pill, beside the name.
 * @param {React.ReactNode} [props.actions] - Row actions. Clicks are kept off the
 *   card's own handler so an action never also navigates.
 * @param {object} [props.sx] - Extra sx merged onto the card.
 */
export default function GridMobileCard({ onClick, leading, primary, secondary, meta, chips, status, actions, sx }) {
  const metaEntries = meta ?? [];
  const chipNodes = chips ?? [];
  const CardRoot = onClick ? ListItemButton : Box;

  return (
    <CardRoot onClick={onClick} sx={{ ...cardSx, ...sx }}>
      <Box sx={headerSx}>
        {leading ? <Box sx={leadingSx}>{leading}</Box> : null}
        <Box sx={identitySx}>
          <Typography component="div" sx={{ ...wrappingTextSx, fontWeight: 'var(--weight-semibold)' }}>
            {primary}
          </Typography>
          {secondary ? (
            <Typography component="div" variant="body2" color="text.secondary" sx={wrappingTextSx}>
              {secondary}
            </Typography>
          ) : null}
        </Box>
        {status ? <Box sx={statusSx}>{status}</Box> : null}
      </Box>

      {metaEntries.length > 0 && (
        <Box sx={bandSx}>
          {metaEntries.map(entry => (
            <Typography key={entry.key} component="div" variant="caption" color="text.secondary" sx={wrappingTextSx}>
              {entry.label}
              {': '}
              <Box component="span" sx={{ color: 'text.primary' }}>
                {entry.value}
              </Box>
            </Typography>
          ))}
        </Box>
      )}

      {chipNodes.length > 0 && <Box sx={{ ...bandSx, gap: 0.5, alignItems: 'center' }}>{chipNodes}</Box>}

      {actions ? (
        <Box sx={{ ...bandSx, gap: 0.5 }} onClick={event => event.stopPropagation()}>
          {actions}
        </Box>
      ) : null}
    </CardRoot>
  );
}
